import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Package, Building2, Briefcase, MessageSquare, ArrowRight, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, href, color }) => (
  <Link 
    to={href}
    className="bg-white border border-slate-200 p-6 hover:border-cyan-400 hover:shadow-md transition-all group"
    data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}
  >
    <div className="flex items-center justify-between mb-4">
      <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${color}`}>
        <Icon className="h-6 w-6" />
      </div>
      <ArrowRight className="h-5 w-5 text-slate-400 group-hover:text-[#0078b7] group-hover:translate-x-1 transition-all" />
    </div>
    <p className="font-heading font-bold text-3xl text-[#0e1c3f] mb-1">{value}</p>
    <p className="text-slate-500 text-sm">{label}</p>
  </Link>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    products: 0,
    realisations: 0,
    jobs: 0,
    messages: 0,
    unreadMessages: 0
  });
  const [recentMessages, setRecentMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [products, realisations, jobs, messages] = await Promise.all([
          axios.get(`${API}/api/admin/products`, { withCredentials: true }),
          axios.get(`${API}/api/admin/realisations`, { withCredentials: true }),
          axios.get(`${API}/api/admin/jobs`, { withCredentials: true }),
          axios.get(`${API}/api/admin/messages`, { withCredentials: true })
        ]);

        const unread = messages.data.filter(m => !m.is_read).length;
        
        setStats({
          products: products.data.length,
          realisations: realisations.data.length,
          jobs: jobs.data.length,
          messages: messages.data.length,
          unreadMessages: unread
        });

        setRecentMessages(messages.data.slice(0, 5));
      } catch (e) {
        console.error('Error fetching stats:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white h-36 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="font-heading font-bold text-2xl text-[#0e1c3f] mb-2">
          Tableau de bord
        </h1>
        <p className="text-slate-600">
          Bienvenue dans l'interface d'administration de SOPREL Groupe
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          icon={Package}
          label="Produits"
          value={stats.products}
          href="/admin/products"
          color="bg-cyan-100 text-[#0078b7]"
        />
        <StatCard 
          icon={Building2}
          label="Réalisations"
          value={stats.realisations}
          href="/admin/realisations"
          color="bg-emerald-100 text-emerald-600"
        />
        <StatCard 
          icon={Briefcase}
          label="Offres d'emploi"
          value={stats.jobs}
          href="/admin/jobs"
          color="bg-violet-100 text-violet-600"
        />
        <StatCard 
          icon={MessageSquare}
          label="Messages"
          value={`${stats.unreadMessages} / ${stats.messages}`}
          href="/admin/messages"
          color="bg-[#ff65c3]/10 text-[#ff65c3]"
        />
      </div>

      {/* Recent Messages */}
      <div className="bg-white border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-heading font-bold text-lg text-[#0e1c3f]">
            Messages récents
          </h2>
          <Button asChild variant="ghost" size="sm" className="text-[#0078b7]">
            <Link to="/admin/messages">Voir tous</Link>
          </Button>
        </div>
        <div className="divide-y divide-slate-100">
          {recentMessages.length > 0 ? recentMessages.map((message) => (
            <div 
              key={message.id}
              className={`px-6 py-4 flex items-start gap-4 ${!message.is_read ? 'bg-cyan-50/50' : ''}`}
            >
              <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                message.is_read ? 'bg-slate-300' : 'bg-[#0078b7]'
              }`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-medium text-[#0e1c3f] truncate">{message.name}</p>
                  <span className="text-slate-400 text-xs">
                    {new Date(message.created_at).toLocaleDateString('fr-FR')}
                  </span>
                </div>
                <p className="text-slate-600 text-sm truncate">{message.subject}</p>
              </div>
            </div>
          )) : (
            <div className="px-6 py-8 text-center text-slate-500">
              Aucun message
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link 
          to="/admin/products"
          className="bg-[#0e1c3f] text-white p-6 hover:bg-[#0e1c3f]/90 transition-colors group"
        >
          <Package className="h-8 w-8 mb-4" />
          <h3 className="font-heading font-bold text-lg mb-2">Gérer les produits</h3>
          <p className="text-slate-300 text-sm">Ajouter, modifier ou supprimer des produits</p>
        </Link>
        <Link 
          to="/admin/realisations"
          className="bg-[#0078b7] text-white p-6 hover:bg-cyan-700 transition-colors group"
        >
          <Building2 className="h-8 w-8 mb-4" />
          <h3 className="font-heading font-bold text-lg mb-2">Gérer les réalisations</h3>
          <p className="text-cyan-100 text-sm">Mettre à jour le portfolio de projets</p>
        </Link>
        <Link 
          to="/admin/content"
          className="bg-slate-100 text-[#0e1c3f] p-6 hover:bg-slate-200 transition-colors group border border-slate-200"
        >
          <FileText className="h-8 w-8 mb-4 text-slate-600" />
          <h3 className="font-heading font-bold text-lg mb-2">Gérer le contenu</h3>
          <p className="text-slate-600 text-sm">Modifier les textes et images du site</p>
        </Link>
      </div>
    </div>
  );
};

export default AdminDashboard;
